<script>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ContactsAPI from 'dashboard/api/contacts';
import MessageAPI from 'dashboard/api/inbox/message';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';

export default {
  name: 'ForwardMessageModal',
  components: {
    NextButton,
    Spinner,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    conversationId: {
      type: Number,
      required: true,
    },
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const searchQuery = ref('');
    const contacts = ref([]);
    const selectedContacts = ref([]);
    const isLoading = ref(false);
    const isSearching = ref(false);
    const isForwarding = ref(false);
    const currentPage = ref(1);
    const totalPages = ref(1);
    const selectAll = ref(false);

    const hasSelectedContacts = computed(() => selectedContacts.value.length > 0);

    const fetchContacts = async (page = 1, search = '') => {
      isSearching.value = true;
      try {
        let response;
        if (search) {
          response = await ContactsAPI.search(search, page, 'name', '');
        } else {
          response = await ContactsAPI.get(page, 'name', '');
        }

        contacts.value = response.data.payload || response.data;
        const meta = response.data.meta || {};
        currentPage.value = meta.current_page || page;
        totalPages.value = Math.ceil((meta.count || contacts.value.length) / 15);
      } catch (error) {
        useAlert(t('FORWARD_MESSAGE.API.ERROR_LOADING'));
      } finally {
        isSearching.value = false;
      }
    };

    const handleSearch = () => {
      currentPage.value = 1;
      fetchContacts(1, searchQuery.value);
    };

    const toggleContact = (contactId) => {
      const index = selectedContacts.value.indexOf(contactId);
      if (index === -1) {
        selectedContacts.value.push(contactId);
      } else {
        selectedContacts.value.splice(index, 1);
      }
      selectAll.value = selectedContacts.value.length === contacts.value.length;
    };

    const toggleSelectAll = () => {
      if (selectAll.value) {
        selectedContacts.value = contacts.value.map(c => c.id);
      } else {
        selectedContacts.value = [];
      }
    };

    const isSelected = (contactId) => {
      return selectedContacts.value.includes(contactId);
    };

    const forwardMessage = async () => {
      if (!hasSelectedContacts.value) return;

      isForwarding.value = true;
      try {
        await MessageAPI.forwardMessage(
          props.conversationId,
          props.message.id,
          selectedContacts.value
        );
        useAlert(t('FORWARD_MESSAGE.API.SUCCESS'));
        emit('close');
        props.onClose();
      } catch (error) {
        const errorMessage = error?.response?.data?.error || t('FORWARD_MESSAGE.API.ERROR');
        useAlert(errorMessage);
      } finally {
        isForwarding.value = false;
      }
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        fetchContacts(page, searchQuery.value);
      }
    };

    const getContactInitials = (contact) => {
      const name = contact.name || contact.email || 'C';
      return name.substring(0, 2).toUpperCase();
    };

    const getContactPhone = (contact) => {
      return contact.phone_number || '---';
    };

    const getLastActivity = (contact) => {
      if (!contact.last_activity_at) return '---';
      const date = new Date(contact.last_activity_at);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return t('FORWARD_MESSAGE.TODAY');
      if (diffDays === 1) return t('FORWARD_MESSAGE.YESTERDAY');
      if (diffDays < 7) return `${diffDays} ${t('FORWARD_MESSAGE.DAYS_AGO')}`;

      return date.toLocaleDateString();
    };

    onMounted(() => {
      fetchContacts();
    });

    return {
      searchQuery,
      contacts,
      selectedContacts,
      isLoading,
      isSearching,
      isForwarding,
      currentPage,
      totalPages,
      selectAll,
      hasSelectedContacts,
      handleSearch,
      toggleContact,
      toggleSelectAll,
      isSelected,
      forwardMessage,
      goToPage,
      getContactInitials,
      getContactPhone,
      getLastActivity,
      t,
    };
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto max-w-2xl">
    <woot-modal-header
      :header-title="t('FORWARD_MESSAGE.TITLE')"
      :header-content="t('FORWARD_MESSAGE.DESCRIPTION')"
    />

    <div class="p-4">
      <!-- Search -->
      <div class="flex gap-2 mb-4">
        <div class="flex-1 relative">
          <span class="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400">
            <fluent-icon icon="search" size="16" />
          </span>
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="t('FORWARD_MESSAGE.SEARCH_PLACEHOLDER')"
            class="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-woot-500"
            @keyup.enter="handleSearch"
          />
        </div>
        <NextButton
          :label="t('FORWARD_MESSAGE.SEARCH')"
          @click="handleSearch"
        />
      </div>

      <!-- Forward Button & Select All -->
      <div class="flex justify-between items-center mb-4">
        <NextButton
          :label="t('FORWARD_MESSAGE.FORWARD')"
          :disabled="!hasSelectedContacts || isForwarding"
          :is-loading="isForwarding"
          @click="forwardMessage"
        />
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            v-model="selectAll"
            type="checkbox"
            class="rounded border-slate-300"
            @change="toggleSelectAll"
          />
          {{ t('FORWARD_MESSAGE.SELECT_PAGE') }}
        </label>
      </div>

      <!-- Loading State -->
      <div v-if="isSearching" class="flex justify-center py-8">
        <Spinner />
      </div>

      <!-- Contacts Table -->
      <div v-else class="border border-slate-200 rounded-lg overflow-hidden">
        <table class="w-full">
          <thead class="bg-slate-50">
            <tr>
              <th class="px-4 py-3 text-left text-sm font-medium text-slate-600">#</th>
              <th class="px-4 py-3 text-left text-sm font-medium text-slate-600">
                {{ t('FORWARD_MESSAGE.TABLE.NAME') }}
              </th>
              <th class="px-4 py-3 text-left text-sm font-medium text-slate-600">
                {{ t('FORWARD_MESSAGE.TABLE.PHONE') }}
              </th>
              <th class="px-4 py-3 text-left text-sm font-medium text-slate-600">
                {{ t('FORWARD_MESSAGE.TABLE.LAST_ACTIVITY') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="contact in contacts"
              :key="contact.id"
              class="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
              @click="toggleContact(contact.id)"
            >
              <td class="px-4 py-3">
                <input
                  type="checkbox"
                  :checked="isSelected(contact.id)"
                  class="rounded border-slate-300"
                  @click.stop="toggleContact(contact.id)"
                />
              </td>
              <td class="px-4 py-3">
                <div class="flex items-center gap-3">
                  <div
                    class="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                    :style="{ backgroundColor: `hsl(${contact.id * 37 % 360}, 70%, 50%)` }"
                  >
                    {{ getContactInitials(contact) }}
                  </div>
                  <span class="text-sm text-slate-800">{{ contact.name || contact.email || 'Sem nome' }}</span>
                </div>
              </td>
              <td class="px-4 py-3 text-sm text-slate-600">
                {{ getContactPhone(contact) }}
              </td>
              <td class="px-4 py-3 text-sm text-slate-500">
                {{ getLastActivity(contact) }}
              </td>
            </tr>
            <tr v-if="contacts.length === 0">
              <td colspan="4" class="px-4 py-8 text-center text-slate-500">
                {{ t('FORWARD_MESSAGE.NO_CONTACTS') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="flex justify-center items-center gap-2 mt-4">
        <NextButton
          ghost
          slate
          sm
          :disabled="currentPage === 1"
          :label="t('FORWARD_MESSAGE.PREV')"
          @click="goToPage(currentPage - 1)"
        />
        <span class="text-sm text-slate-600">
          {{ currentPage }} / {{ totalPages }}
        </span>
        <NextButton
          ghost
          slate
          sm
          :disabled="currentPage === totalPages"
          :label="t('FORWARD_MESSAGE.NEXT')"
          @click="goToPage(currentPage + 1)"
        />
      </div>
    </div>

    <!-- Footer -->
    <div class="flex justify-end gap-2 p-4 border-t border-slate-200">
      <NextButton
        faded
        slate
        :label="t('FORWARD_MESSAGE.CANCEL')"
        @click="onClose"
      />
    </div>
  </div>
</template>
